import { Pipe, PipeTransform, inject } from '@angular/core';
import { EnumMeasureTypes } from '@library/store/measures/measures.interface';
import { UserFacade } from '@library/store/user/user.facade';
import { User } from '@library/store/user/user.interface';
import { Enums } from '@library/utils/constants/Enums.constant';
import { take } from 'rxjs';

@Pipe({
  name: 'toUserUnit',
  standalone: false,
})
export class ToUserUnitPipe implements PipeTransform {
  private readonly userFacade = inject(UserFacade);

  transform(value: number, measureType: EnumMeasureTypes): number {
    let user: User;
    this.userFacade.currentUser$.pipe(take(1)).subscribe(u => user = u);
    if (value === null || typeof value === 'undefined') {
      return value;
    }

    switch (measureType) {
      case EnumMeasureTypes.TEMPERATURE:
      case EnumMeasureTypes.MAX_TEMPERATURE:
      case EnumMeasureTypes.MIN_TEMPERATURE:
      case EnumMeasureTypes.SP_TEMPERATURE:
        return UNIT_CONVERSION.TEMPERATURE[user.unit_system](value);

      case EnumMeasureTypes.PRESSURE:
      case EnumMeasureTypes.MIN_PRESSURE:
      case EnumMeasureTypes.MAX_PRESSURE:
        return UNIT_CONVERSION.PRESSURE[user.unit_pressure ?? 0](value);

      case EnumMeasureTypes.WIND_STRENGTH:
      case EnumMeasureTypes.GUST_STRENGTH:
        return UNIT_CONVERSION.WIND_SPEED[user.unit_wind](value);

      case EnumMeasureTypes.SUM_RAIN:
        return UNIT_CONVERSION.RAIN[user.unit_system](value);

      default:
        return value;
    }
  }
}
const UNIT_CONVERSION = {
  WIND_SPEED: {
    [Enums.WindUnit.KMH]: (speed: number) => speed,
    [Enums.WindUnit.MPH]: (speed: number) => speed / 1.609344,
    [Enums.WindUnit.MS]: (speed: number) => speed / 3.6,
    [Enums.WindUnit.BEAUFORT]: (speed: number) => kphToBeaufort(speed),
    [Enums.WindUnit.KNOT]: (speed: number) => speed / 1.852,
  },
  TEMPERATURE: {
    [Enums.SystemUnit.METRIC]: (temperature: number) => temperature,
    [Enums.SystemUnit.IMPERIAL]: (temperature: number) => (temperature * 9 / 5) + 32,
  },
  RAIN: {
    [Enums.SystemUnit.METRIC]: (rain: number) => rain,
    [Enums.SystemUnit.IMPERIAL]: (rain: number) => rain / 25.4,
  },
  PRESSURE: {
    [Enums.PressureUnit.MBAR]: (pressure: number) => pressure,
    [Enums.PressureUnit.MERCURY]: (pressure: number) => pressure / 33.864,
    [Enums.PressureUnit.TORR]: (pressure: number) => pressure / 1.3332236842,
  },
};

function kphToBeaufort(kph: number) {
  // Define Beaufort scale wind speed ranges in km/h
  const beaufortScale = [
    { min: 0, max: 1, beaufort: 0 },
    { min: 1, max: 5, beaufort: 1 },
    { min: 6, max: 11, beaufort: 2 },
    { min: 12, max: 19, beaufort: 3 },
    { min: 20, max: 28, beaufort: 4 },
    { min: 29, max: 38, beaufort: 5 },
    { min: 39, max: 49, beaufort: 6 },
    { min: 50, max: 61, beaufort: 7 },
    { min: 62, max: 74, beaufort: 8 },
    { min: 75, max: 88, beaufort: 9 },
    { min: 89, max: 102, beaufort: 10 },
    { min: 103, max: 117, beaufort: 11 },
    { min: 118, max: Number.MAX_SAFE_INTEGER, beaufort: 12 }, // For any speed greater than 118 km/h
  ];

  // Find the Beaufort scale category for the given kph
  for (let i = 0; i < beaufortScale.length; i++) {
    if (kph >= beaufortScale[i].min && kph <= beaufortScale[i].max) {
      const beaufort = beaufortScale[i].beaufort;
      return beaufort;
    }
  }

  return -1; // Return -1 if the speed is outside the Beaufort scale range
}
